import { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import {
    BodyTextSmall,
    CalloutInline,
    HeadingText30,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { TrackClick, AnalyticsProperties } from '@modernhealth/web-analytics';
import { InternalLinkNode } from 'src/generated/webapp_gql';
import { Avatar } from 'src/modules/messaging/components/Avatar';
import { DropdownMenu, DropdownMenuItem } from 'src/shared/components/DropdownMenu';
import { ColorScheme } from '@modernhealth/graphql';
import { filterNullish } from '@modernhealth/filter-nullish';
import { useTranslation } from 'react-i18next';
import { InternalLinkButton } from 'src/shared/analytics/components';

const ActionItem: FC<{
    path?: string;
    clickEvent: {
        name: string;
        properties: AnalyticsProperties;
    };
}> = ({ path, clickEvent, children }) => {
    if (path) {
        return (
            <LinkActionItem
                eventName={clickEvent.name}
                eventProperties={clickEvent.properties}
                to={path}
                variant="flat"
                fluid
            >
                {children}
            </LinkActionItem>
        );
    }

    return <ProviderActionItemWrapper role="region">{children}</ProviderActionItemWrapper>;
};

export interface ProviderActionsProps {
    action: InternalLinkNode;
    /**
     * Zero-based index necessary for analytics tracking. If this component is being used outside
     * of any array, starting elementIndex should be set to 0
     */
    elementIndex: number;
}
export const ProviderAction: FC<ProviderActionsProps> = ({ action, elementIndex }) => {
    const { t } = useTranslation();

    /**
     *  This calculates the maxWidth for the subtitle
     *  if the action has childLinks.
     */
    const subTitleMaxWidth = (action: InternalLinkNode) => {
        let maxWidth = '100%';
        if (action.childLinks && action.subtitle && action.subtitle.length > 40) maxWidth = '90%';
        return maxWidth;
    };
    const isLink = action.path.includes('/');
    const [primaryLink, ...nullableChildLinks] = Array.isArray(action.childLinks)
        ? action.childLinks
        : [];
    const childLinks = useMemo(() => filterNullish(nullableChildLinks), [nullableChildLinks]);
    const hasChildLinks = childLinks.length > 0;
    const overlayImage = action.images?.[0];
    const hasLink = isLink && !hasChildLinks;

    return (
        <>
            <ActionItem
                path={hasLink ? action.path : undefined}
                clickEvent={{
                    name: action.titleTranslationKey,
                    properties: {
                        elementIndex,
                    },
                }}
            >
                <ImageContainer>
                    {overlayImage && <IconContainer src={overlayImage} alt="" />}
                    <ProviderAvatar
                        image={action.image ?? undefined}
                        diameter={60}
                        initials={action.providerInitials ?? undefined}
                        grayscale={action.colorScheme === ColorScheme.Grayscale}
                    />
                </ImageContainer>
                <ItemContent>
                    <HeadingText30>{action.title}</HeadingText30>
                    <BodyTextWrapper hasLink={hasLink}>
                        <BodyText hasLink={hasLink} maxWidth={subTitleMaxWidth(action)}>
                            {action.subtitle}
                        </BodyText>
                    </BodyTextWrapper>
                </ItemContent>
                {!!primaryLink || hasChildLinks ? (
                    <>
                        {!!primaryLink && (
                            <InternalLinkButton
                                role="link"
                                size="medium"
                                disabled={primaryLink.disabled || false}
                                variant="secondary"
                                css={{
                                    marginLeft: 'auto',
                                    marginRight: pxToRem(10),
                                }}
                                eventName={primaryLink.titleTranslationKey}
                                to={primaryLink.path}
                            >
                                {primaryLink.title}
                            </InternalLinkButton>
                        )}
                        {hasChildLinks && (
                            <DropdownMenu
                                buttonTitle={t(
                                    'messaging:conversation.header.more_provider_actions',
                                )}
                                popperAriaLabel={t(
                                    'messaging:conversation.header.more_provider_actions.menu',
                                )}
                            >
                                {childLinks.map((link, index) => (
                                    <TrackClick
                                        key={link.title}
                                        eventName={link.titleTranslationKey}
                                        eventProperties={{
                                            element_index: index,
                                        }}
                                    >
                                        <DropdownMenuItem
                                            title={link.title}
                                            linkTo={link.path}
                                            color={link.colorScheme === 'RED' ? 'red55' : 'gray65'}
                                        />
                                    </TrackClick>
                                ))}
                            </DropdownMenu>
                        )}
                    </>
                ) : (
                    <Icon name="chevron-right" css={{ marginLeft: 'auto' }} size={20} />
                )}
            </ActionItem>
            {primaryLink?.additionalInformationTitle && primaryLink?.additionalInformation && (
                <CalloutInline
                    variant="default"
                    css={{ marginBottom: pxToRem(20) }}
                    heading={primaryLink?.additionalInformationTitle}
                >
                    {primaryLink?.additionalInformation}
                </CalloutInline>
            )}
        </>
    );
};

const ProviderAvatar = styled(Avatar)<{ grayscale: boolean }>(({ grayscale }) => ({
    borderColor: colors.violet40,
    backgroundColor: colors.violet40,
    filter: grayscale ? 'grayscale(1)' : undefined,
}));

const ProviderActionItemWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
});

const BodyText = styled(BodyTextSmall)<{ hasLink: boolean }>(({ hasLink }) => ({
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: hasLink ? 'break-spaces' : 'nowrap',
}));

const BodyTextWrapper = styled.div<{ hasLink: boolean }>(({ hasLink }) => ({
    overflow: 'hidden',
    whiteSpace: 'break-spaces',
    maxWidth: hasLink ? undefined : pxToRem(300),
}));

const ItemContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: pxToRem(16),
});

const LinkActionItem = styled(ProviderActionItemWrapper.withComponent(InternalLinkButton))({
    padding: 0,
    margin: 0,
});

const IconContainer = styled.img({
    position: 'absolute',
    right: 0,
    bottom: 0,
});

const ImageContainer = styled.div({
    display: 'block',
    marginRight: pxToRem(10),
    position: 'relative',
});
